'use client';
import React from 'react';
import { Brain, Sun, Phone, Zap, Moon, Users, Sparkles, ArrowRight } from 'lucide-react';

const outcomes = [
  {
    icon: Users,
    title: 'Never Forget a Client Again.',
    desc: 'AgentOS remembers every conversation, every promise, every follow-up. Your entire client history — organized and searchable — always at your fingertips.',
    color: '#00D4FF',
    bg: 'rgba(0,212,255,0.08)',
    border: 'rgba(0,212,255,0.18)',
  },
  {
    icon: Sun,
    title: 'Start Every Morning Prepared.',
    desc: 'Wake up to a Morning Brief that tells you exactly who needs attention, what\'s due today, and where your best opportunities are. No more guessing.',
    color: '#A78BFA',
    bg: 'rgba(167,139,250,0.08)',
    border: 'rgba(167,139,250,0.18)',
  },
  {
    icon: Phone,
    title: 'Know Exactly Who To Call Next.',
    desc: 'AI analyzes your pipeline and surfaces the highest-priority contacts. Stop wondering who to reach out to. Start every call with confidence.',
    color: '#00D4FF',
    bg: 'rgba(0,212,255,0.07)',
    border: 'rgba(0,212,255,0.15)',
  },
  {
    icon: Zap,
    title: 'Respond Faster Than Your Competition.',
    desc: 'Draft follow-up emails, listing descriptions, and client messages in seconds. AI writes in your voice. You review and send. Speed wins deals.',
    color: '#22C55E',
    bg: 'rgba(34,197,94,0.07)',
    border: 'rgba(34,197,94,0.15)',
  },
  {
    icon: Moon,
    title: 'End Every Day Organized.',
    desc: 'The Wrap Up feature closes your day cleanly. Log what happened, set tomorrow\'s priorities, and leave the office knowing nothing slipped through.',
    color: '#A78BFA',
    bg: 'rgba(167,139,250,0.07)',
    border: 'rgba(167,139,250,0.15)',
  },
  {
    icon: Brain,
    title: 'AI That Remembers Your Entire Business.',
    desc: 'Every showing. Every email. Every interaction. AgentOS builds context across your entire business so you never lose the thread — even months later.',
    color: '#00D4FF',
    bg: 'rgba(0,212,255,0.06)',
    border: 'rgba(0,212,255,0.14)',
  },
];

export default function FeaturesSection() {
  return (
    <section id="features" className="py-24 relative overflow-hidden" style={{ background: '#0B0F19' }}>
      <div
        className="absolute top-0 left-0 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(0,212,255,0.05) 0%, transparent 70%)', filter: 'blur(60px)' }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-16">
          <div className="section-badge mb-4">
            <Sparkles size={12} />
            What AgentOS Does For You
          </div>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-4"
            style={{ color: '#F0F4FF' }}
          >
            Built around{' '}
            <span className="gradient-cyan-text">your outcomes.</span>
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: 'rgba(240,244,255,0.55)' }}>
            Not features. Not modules. Real results that change how you work every single day.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {outcomes.map((outcome) => (
            <div
              key={outcome.title}
              className="rounded-2xl p-7 card-hover cursor-default flex flex-col gap-4"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.background = outcome.bg;
                (e.currentTarget as HTMLElement).style.borderColor = outcome.border;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.04)';
                (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.08)';
              }}
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: outcome.bg, border: `1px solid ${outcome.border}` }}
              >
                <outcome.icon size={22} style={{ color: outcome.color }} />
              </div>
              <div>
                <h3 className="text-base font-bold mb-2 leading-snug" style={{ color: '#F0F4FF' }}>{outcome.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(240,244,255,0.55)' }}>{outcome.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <a
            href="https://agentos-ai-vgojf93.public.builtwithrocket.new/"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center gap-2"
          >
            See It In Action
            <ArrowRight size={16} />
          </a>
        </div>
      </div>
    </section>
  );
}
